import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import GenericTagComp from "../../genericComponents/tag/GenericTagComp";


const ResizableDivForTag = ({ items = [], handleSideCard, childSc, rowData, countOnly = false }) => {
  const [visibleCount, setVisibleCount] = useState(0);
  const [readyToRender, setReadyToRender] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const containerRef = useRef(null);
  const moreRef = useRef(null);

  const filteredItems = items.filter(item => !item?.dontShow); // ✅ core filtered list

  const getTagColorPerTxt = (text) => {
    switch (text) {
      case "Commercial Airplanes":
      case "Defense":
      case "Nudge":  
      case "in-progress":  
        return "blue";
      case "New Growth Area":
      case "Higher Spending":  
      case "increased":
      case "Mission Critical Priority":
      case "Strategic Market Expansion":
      case "High":   
        return "green";
      case "Pain point":
      case "Active Challenge":
      case "Low":  
        return "red";  
      case "Medium":    
        return "yellow";
      default:
        return "gray";
    }
  };

  // Calculate how many filtered items fit
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const calculateVisible = () => {
      const containerWidth = container.getBoundingClientRect().width;
      const children = Array.from(container.querySelectorAll(".child-container"));
      let total = 0;
      let count = 0;

      for (let i = 0; i < children.length; i++) {
        const width = children[i].getBoundingClientRect().width;
        if (total + width <= containerWidth) {
          total += width;
          count++;
        } else {
          break;
        }
      }

      setVisibleCount(count);
      setReadyToRender(true);
    };

    const observer = new ResizeObserver(() => calculateVisible());
    observer.observe(container);
    calculateVisible();

    return () => observer.disconnect();
  }, [items]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const dropdownEl = document.querySelector(".tag-overflow-dropdown");
      if (
        dropdownEl &&
        !dropdownEl.contains(event.target) &&
        !moreRef.current?.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDropdown]);

  const handleMoreClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const rect = moreRef.current?.getBoundingClientRect();
    if (rect) {
      setDropdownStyle({
        position: "absolute",
        top: `${rect.bottom + window.scrollY + 4}px`,
        left: `${rect.left + window.scrollX}px`,
        backgroundColor: "#fff",
        boxShadow: "0 0.25rem 0.75rem rgba(0, 0, 0, 0.15)",
        borderRadius: "0.375rem",
        padding: "0.5rem",
        minWidth: "9.375rem",
      });
      setShowDropdown(true);
    }
  };

  const visibleItems = filteredItems.slice(0, visibleCount);
  const overflowItems = filteredItems.slice(visibleCount); // ✅ includes only visible + filtered

  const Dropdown = showDropdown
    ? ReactDOM.createPortal(
        <div
          className="tag-overflow-dropdown"
          style={dropdownStyle}
          onMouseLeave={() => setShowDropdown(false)}
        >
          {overflowItems.map((item, index) => (
            <div key={`dropdown-${index}`} className="tag-overflow-dropdown-item">
              <GenericTagComp
                color={getTagColorPerTxt(item?.label)}
                text={item.label}
                tagIcon={item.tagIcon}
                onChipClick={(e) => {
                  handleSideCard(e, rowData, item.key, childSc);
                  setShowDropdown(false);
                  item?.onChipClick?.();
                }}
              />
            </div>
          ))}
        </div>,
        document.body
      )
    : null;

  return (
    <div
      ref={containerRef}
      style={{
        width: "95%",
        position: "relative",
        boxSizing: "border-box",
      }}
    >
      {/* Hidden children for width calculation */}
      <div style={{ position: "absolute", visibility: "hidden", height: 0 }}>
        {filteredItems.map((item, index) => (
          <div key={`measure-${index}`} className="child-container">
            <GenericTagComp
              color={getTagColorPerTxt(item?.label)}
              text={item?.label}
              tagIcon={item?.tagIcon}
              onChipClick={() => null}
            />
          </div>
        ))}
      </div>

      {/* Render visible tags */}
      <div style={{ display: "inline-flex", flexWrap: "nowrap", gap: "0.1rem" }}>
        {readyToRender &&
          visibleItems.map((item, index) => (
            <div className="child-container" key={index}>
              <GenericTagComp
                color={getTagColorPerTxt(item?.label)}
                text={item?.label}
                tagIcon={item?.tagIcon}
                onChipClick={(e) => handleSideCard(e, rowData, item.key, childSc)}
              />
            </div>
          ))}
        {readyToRender && overflowItems.length > 0 && (
          <div ref={moreRef} className={`${countOnly ? 'border-0' : ''}`}>
            <GenericTagComp
              color={"gray"}
              text={countOnly ? `( ${overflowItems.length} )` : `+${overflowItems.length}`}
              onChipClick={handleMoreClick}
            />
          </div>
        )}
      </div>

      {Dropdown}
    </div>
  );
};

export default ResizableDivForTag;
